import { ResourceId } from '@wix/wix-help-widget-common/homepage';
import {
  AIChatbot,
  Article,
  Code,
  GetStarted,
  IconProps,
  Preview,
  WixForum,
  WixSocialPosts,
} from '@wix/wix-ui-icons-common';
import { Reels } from '@wix/wix-ui-icons-common/on-stage';
import { AnchorHTMLAttributes, FC } from 'react';

import { TLocalesKeys } from '../../../../types';
import { ButtonClicked } from '../../../bi';
import { appRoutes } from '../../../routes';
import { externalResourcesDataHooks } from './ExternalResources.dataHooks';
import { CustomDiscords } from './icons/CustomDiscord';

type ExternalResourcesLink = Readonly<{
  href: string;
  target?: AnchorHTMLAttributes<HTMLAnchorElement>['target']; // optional; default is '_blank'
  appRoute?: never;
}>;

type ExternalResourcesRoute = Readonly<{
  appRoute: string;
  href?: never;
  target?: never;
}>;

export type ExternalResourcesContent = (ExternalResourcesLink | ExternalResourcesRoute) &
  Readonly<{
    IconComponent: FC<IconProps>;
    titleKey: TLocalesKeys;
    dataHook: string;
    buttonClickedBiName: ButtonClicked;
    descriptionKey?: TLocalesKeys;
  }>;

export const mapResourceIdToContent: Record<ResourceId, ExternalResourcesContent> = {
  [ResourceId.WixLearn]: {
    IconComponent: Reels,
    titleKey: 'home_resources_wix_learn_title',
    descriptionKey: 'home_resources_wix_learn_description',
    href: 'https://www.wix.com/learn',
    dataHook: externalResourcesDataHooks.wixLearn(),
    buttonClickedBiName: ButtonClicked.HomeResourcesWixLearn,
  },
  [ResourceId.WixBlog]: {
    IconComponent: WixSocialPosts,
    titleKey: 'home_resources_wix_blog_title',
    descriptionKey: 'home_resources_wix_blog_description',
    href: 'https://www.wix.com/blog',
    dataHook: externalResourcesDataHooks.wixBlog(),
    buttonClickedBiName: ButtonClicked.HomeResourcesWixBlog,
  },
  [ResourceId.SEOLearningHub]: {
    IconComponent: Preview,
    titleKey: 'home_resources_seo_learning_hub_title',
    descriptionKey: 'home_resources_seo_learning_hub_description',
    href: 'https://www.wix.com/seo/learn',
    dataHook: externalResourcesDataHooks.seoLearningHub(),
    buttonClickedBiName: ButtonClicked.HomeResourcesSeo,
  },
  [ResourceId.VeloHub]: {
    IconComponent: Code,
    titleKey: 'home_resources_velo_hub_title',
    descriptionKey: 'home_resources_velo_hub_description',
    href: 'https://www.wix.com/velo',
    dataHook: externalResourcesDataHooks.veloHub(),
    buttonClickedBiName: ButtonClicked.HomeResourcesVelo,
  },
  [ResourceId.PartnersForum]: {
    IconComponent: WixForum,
    titleKey: 'home_resources_partners_forum_title',
    descriptionKey: 'home_resources_partners_forum_description',
    href: 'https://community.wix.com/partners/forum',
    dataHook: externalResourcesDataHooks.partnersForum(),
    buttonClickedBiName: ButtonClicked.HomeResourcesPartners,
  },
  [ResourceId.CustomerCareTickets]: {
    IconComponent: WixSocialPosts,
    titleKey: 'home_resources_customer_care_tickets_title',
    descriptionKey: 'home_resources_customer_care_tickets_description',
    href: 'https://manage.wix.com/studio/partner-tickets',
    dataHook: externalResourcesDataHooks.customerCareTickets(),
    target: '_top',
    buttonClickedBiName: ButtonClicked.HomeResourcesCustomerCareTickets,
  },
  [ResourceId.CustomerCareTicketsLean]: {
    IconComponent: WixSocialPosts,
    titleKey: 'home_resources_customer_care_tickets_title',
    href: 'https://manage.wix.com/studio/partner-tickets',
    dataHook: externalResourcesDataHooks.customerCareTickets(),
    buttonClickedBiName: ButtonClicked.HomeResourcesCustomerCareTickets,
  },
  [ResourceId.HireAnotherProfessional]: {
    IconComponent: GetStarted,
    titleKey: 'home_resources_hire_professional_title',
    descriptionKey: 'home_resources_hire_professional_description',
    href: 'https://www.wix.com/marketplace/hire',
    dataHook: externalResourcesDataHooks.hireAnotherProfessional(),
    buttonClickedBiName: ButtonClicked.HomeResourcesHireProfessional,
  },
  [ResourceId.HireAnotherProfessionalLean]: {
    IconComponent: GetStarted,
    titleKey: 'home_hire_a_professional',
    href: 'https://www.wix.com/marketplace',
    dataHook: externalResourcesDataHooks.hireAnotherProfessional(),
    buttonClickedBiName: ButtonClicked.HomeResourcesHireProfessional,
  },
  [ResourceId.CommunityForum]: {
    IconComponent: WixForum,
    titleKey: 'home_resources_community_forum_title',
    descriptionKey: 'home_resources_community_forum_description',
    href: 'https://www.wix-forum-community.com',
    dataHook: externalResourcesDataHooks.communityForum(),
    buttonClickedBiName: ButtonClicked.HomeResourcesCommunityForum,
  },
  [ResourceId.StudioCommunityForum]: {
    IconComponent: WixForum,
    titleKey: 'home_resources_studio_community_forum_title',
    descriptionKey: 'home_resources_studio_community_forum_description',
    href: 'https://forum.wixstudio.com/c/ask-the-community/8',
    dataHook: externalResourcesDataHooks.studioCommunityForum(),
    buttonClickedBiName: ButtonClicked.HomeResourcesCommunityForumStudio,
  },
  [ResourceId.WixDevelopers]: {
    IconComponent: Code,
    titleKey: 'home_resources_wix_developers_title',
    descriptionKey: 'home_resources_wix_developers_description',
    href: 'https://dev.wix.com/api/sdk/about-wix-headless/overview',
    dataHook: externalResourcesDataHooks.wixDevelopers(),
    buttonClickedBiName: ButtonClicked.HomeResourcesWixDevelopers,
  },
  [ResourceId.WixDeveloperDocs]: {
    IconComponent: Article,
    titleKey: 'home_resources_wix_docs_title',
    descriptionKey: 'home_resources_wix_docs_description',
    href: 'https://dev.wix.com/docs/',
    dataHook: externalResourcesDataHooks.wixDeveloperDocs(),
    buttonClickedBiName: ButtonClicked.HomeResourcesWixDeveloperDocs,
  },
  [ResourceId.WixDeveloperCommunity]: {
    IconComponent: CustomDiscords,
    titleKey: 'home_resources_wix_community_title',
    descriptionKey: 'home_resources_wix_community_description',
    href: 'https://discord.com/invite/devs-on-wix',
    dataHook: externalResourcesDataHooks.wixDeveloperCommunity(),
    buttonClickedBiName: ButtonClicked.HomeResourcesWixDeveloperCommunity,
  },
  [ResourceId.ChatWithUs]: {
    IconComponent: AIChatbot,
    titleKey: 'home_resources_chatbot_title',
    descriptionKey: 'home_resources_chatbot_description',
    appRoute: appRoutes.chatbot(),
    dataHook: externalResourcesDataHooks.chatWithUs(),
    buttonClickedBiName: ButtonClicked.HomeResourcesChatWithUs,
  },
};
